import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Theme,
  Typography
} from '@mui/material'
import { colorCodes, ColorKeysWithId } from '../subScreens/EditorDashboardDetails'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SubHeadline
  from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/SubHeadline'
import Statistic from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Statistic'
import ImportProfessocialPost from '../../../../shared/components/ImportComponents/ImportProfessocialPost'
import { ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import ForumIcon from '@mui/icons-material/Forum'
import makeStyles from '@mui/styles/makeStyles/makeStyles'
import {
  DeferredPost,
  DeferredPostStatus,
  NetworkType,
  PublishedPost,
  PublishedPostWithPostContent
} from '@3m5/sm_publish-frontend-api'
import { selectFacebookPostByPostId, selectInstagramPostByPostId } from '../../../../store/dashboard/dashboard.selector'
import { useTranslation } from 'react-i18next'
import ProfileImage
  from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/ProfileImage'
import LinkHeadline
  from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/LinkHeadline'
import Comments
  from '@professocial/professocialPosting/src/shared/components/ModuleComponents/Post/modules/Comments/Comments'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'

interface IProps {
  networkType?: NetworkType;
  deferredPost?: DeferredPost;
  publishedPost?: PublishedPost;
  getFacebookPost?: (posting: PublishedPost) => void
  getInstagramPost?: (posting: PublishedPost) => void
  colored?: ColorKeysWithId[];
  theme: Theme;
  user: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    // padding: theme.spacing(4),
    marginBottom: '10px',
  },
  icon: {
    verticalAlign: 'top',
    fill: theme.palette.info.main,
  },
  root: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'inherit',
    paddingTop: '9px',
    width: '100%',
  },
  button: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'initial',
    marginRight: '20px',
  },
}))

const EditorDashboardDetailsPost: React.FC<IProps> = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const id = props.deferredPost?.id || props.publishedPost?.id
  const networkPageId = props.deferredPost?.networkPageId || props.publishedPost?.networkPageId
  const postId = props.deferredPost?.id || props.publishedPost?.id
  const source = props.deferredPost?.source || props.publishedPost?.source
  const status = props.deferredPost?.status
  const networkType = props.deferredPost?.networkType || props.publishedPost?.networkType

  const networkPostId = props.publishedPost?.networkPostId
  const facebookPost = useSelector(selectFacebookPostByPostId(networkPostId))
  const instagramPost = useSelector(selectInstagramPostByPostId(networkPostId))

  const [publishedPost, setPublishedPost] = useState<PublishedPostWithPostContent>()
  const [expanded, setExpanded] = React.useState<string | false>(false)
  const [showComments, setShowComments] = React.useState(false)

  const handleChange = (panel: string | undefined) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    panel && setExpanded(isExpanded ? panel : false)
  }
  const handleShowComments = () => {
    setShowComments(!showComments)
  }

  useEffect(() => {
    if (props.publishedPost) {
      props.getFacebookPost && props.getFacebookPost(props.publishedPost)
      props.getInstagramPost && props.getInstagramPost(props.publishedPost)
    }
  }, [props.publishedPost])

  useEffect(() => {
    if (facebookPost && props.publishedPost) {
      const { image, video, link, extensions, headline, text } = facebookPost.content
      setPublishedPost({
        publishedPost: props.publishedPost,
        postContent: {
          image,
          video,
          text,
          link,
          extensions,
          headline,
        },
      })
    }
    if (instagramPost && props.publishedPost) {
      const { image, video, link, extensions, headline, text } = instagramPost.content[0]
      setPublishedPost({
        publishedPost: props.publishedPost,
        postContent: {
          image,
          video,
          text,
          link,
          extensions,
          headline,
        },
      })
    }
  }, [facebookPost, instagramPost, props.publishedPost])

  const colored = props.colored?.find(item => item.id === id)
  const color = colorCodes.find(item => colored && item.key === colored.key)?.color

  return (
    <Paper className={classes.paper}>
      <Accordion sx={{ border: 'none', borderLeft: `9px solid ${color}` }} expanded={expanded === postId}
        onChange={handleChange(postId)}>
        <AccordionSummary expandIcon={<KeyboardArrowDownIcon/>}
          sx={{ '& .MuiAccordionSummary-content': { margin: '20px 0' } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%"
            sx={{ marginRight: '40px' }}>
            <Stack direction="row">
              <ProfileImage
                data={`https://graph.facebook.com/${props.deferredPost?.networkPageId || publishedPost?.publishedPost.networkPageId}/picture`}
              />
              <Stack direction="column" justifyContent="center">
                <LinkHeadline
                  networkPageId={networkPageId}
                  post={facebookPost || instagramPost}
                  networkType={props.deferredPost?.networkType || props.publishedPost?.networkType}
                  value={{
                    url: facebookPost
                      ? `https://www.facebook.com/${props.publishedPost?.networkPageId}`
                      : instagramPost
                        ? `https://www.instagram.com/${instagramPost?.postedByNetworkName}/`
                        : '',
                    label: props.deferredPost?.networkPageName || props.publishedPost?.networkPageName,
                  }}
                />
                <Stack direction="row" spacing={3} alignItems="center">
                  <SubHeadline
                    networkPageId={networkPageId}
                    post={undefined}
                    value={t(`coreDashboard:${source}`)}
                  />

                  {status && status === DeferredPostStatus.ErrorWhilePosting &&
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ lineHeight: '1rem', '& .MuiChip-iconSmall': { marginLeft: '5px', marginBottom: '1px' } }}
                      label={t('coreDashboard:ErrorWhilePosting')}
                      icon={<WarningRoundedIcon/>}
                    />}

                  {status && status === DeferredPostStatus.AwaitingUserConfirmation && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ lineHeight: '1rem', '& .MuiChip-iconSmall': { marginLeft: '5px', marginBottom: '1px' } }}
                      label={t('coreDashboard:AwaitingUserConfirmation')}
                      icon={<HourglassBottomRoundedIcon />}
                    />
                  )}

                  {status && status === DeferredPostStatus.NoActiveUsersForPage && (
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ lineHeight: '1rem', '& .MuiChip-iconSmall': { marginLeft: '5px', marginBottom: '1px' } }}
                      label={t('coreDashboard:NoActiveUsersForPage')}
                      icon={<WarningRoundedIcon />}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
            <Box>
              {!props.deferredPost && (
                <Statistic
                  value={props.networkType === NetworkType.Facebook ? {
                    postEngagedUsers: facebookPost?.postStatistics?.postEngagedUsers,
                    postReactionsLikeTotal: facebookPost?.postStatistics?.postReactionsLikeTotal,
                    postImpressions: facebookPost?.postStatistics?.postImpressions,
                  } : {
                    commentCount: instagramPost?.postStatistics?.commentCount,
                    engagement: instagramPost?.postStatistics?.engagement,
                    impressions: (instagramPost?.postStatistics?.impressions || 0) + (instagramPost?.postStatistics?.videoViews || 0),
                    likeCount: instagramPost?.postStatistics?.likeCount,
                    reach: instagramPost?.postStatistics?.reach,
                  }}
                  networkType={props.networkType}
                  justifyContent="end"
                  view={ViewEnum.single}
                  post={facebookPost || instagramPost}
                />
              )}
            </Box>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ marginLeft: '57px' }}>
          <Grid container sx={{ position: 'relative' }}>
            <ImportProfessocialPost
              postConfiguration={{
                theme: props.theme,
                userRole: props.user.role,
                size: ScreenEnum.xs,
                view: ViewEnum.templateDetails,
                publishedPost: publishedPost,
                deferredPost: props.deferredPost,
              }}
            />
            {!props.publishedPost && <Box sx={{ mb: 3 }}/>}
            {props.publishedPost && (
              <Grid container item spacing={2}>
                <Grid item xs={12}><Divider/></Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <ButtonComponent
                        text={
                          <>
                            <span style={{ paddingRight: '6px' }}>
                              <ForumIcon className={classes.icon} width="23px"/>
                            </span>{t(`coreDashboard:${showComments ? 'hideComments' : 'showComments'}`)}
                          </>
                        }
                        variant="text"
                        color="info"
                        className={classes.root}
                        onClick={handleShowComments}
                        disableElevation
                      />
                    </Box>
                    <Box>
                      <Link
                        href={props.networkType === NetworkType.Facebook
                          ? `https://www.facebook.com/${props.publishedPost.networkPostId}`
                          : instagramPost?.networkPostUrl}
                        target="_blank" style={{ textDecoration: 'none' }}
                      >
                        <ButtonComponent
                          text={t('coreDashboard:toPost')}
                          variant="contained"
                          color="primary"
                        />
                      </Link>
                    </Box>
                  </Stack>
                </Grid>
                <Collapse in={showComments} collapsedSize={10}>
                  {facebookPost?.comments?.length === 0 || instagramPost?.comments?.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: '100px',
                      }}
                      >
                        <Typography sx={{ p: 2 }}
                          variant="body1">{t('postCommon:noCommentsYet')}</Typography>
                      </Box>
                    </Grid>
                  ) : (
                    <Box sx={{ px: 3, pb: 3 }}>
                      <Comments
                        networkPageId={networkPageId}
                        post={facebookPost || instagramPost}
                        networkType={networkType}
                      />
                    </Box>)}
                </Collapse>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Paper>
  )
}

export default EditorDashboardDetailsPost
